import React from "react";
import { useState, useEffect } from "react";
import { Box, Paper, useTheme, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { tokens } from "../../../assets/theme";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { useNavigate } from "react-router-dom";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { svSE, enUS } from '@mui/x-data-grid/locales';
import { FormattedMessage, useIntl } from "react-intl";
import messages from "./messages";
import AxionControls from "../../../components/controls/AxionControls";
import Checkbox from "../../../components/controls/Checkbox";
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';

export default function SensorDataDiagramSettingsGrid(props) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const apiRef = useGridApiRef();
    const { formatMessage } = useIntl();
    const language = localStorage.getItem('language') || 'en';
    const translations = language === "sv" ? svSE : enUS;
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
    const [sortModelStorage, setSortModelStorage] = useState([
        { field: "sortOrder", sort: "asc" }
    ]);

    let columns = [
          { 
            field: "name", 
            headerName: formatMessage(messages.readingTypeNameColumn),
            flex: 1,
          },
          { 
            field: "description", 
            headerName: formatMessage(messages.readingTypeDescriptionColumn),
            flex: 1, 
          },
          { 
            field: "channel", 
            headerName: formatMessage(messages.channelColumn),
            flex: 0.8, 
          },
          { 
            field: "axis", 
            headerName: formatMessage(messages.axisColumn),
            flex: 0.5, 
          },
          { 
            field: "dataTypeId", 
            headerName: formatMessage(messages.dataTypeColumn),
            flex: 1, 
            renderCell: ({ row }) => props.dataTypesResult.data?.find((dataType) => dataType.id === row.dataTypeId)?.dataType,
          },
          { 
            field: "sensorId", 
            headerName: formatMessage(messages.sensorColumn),
            flex: 0.5,  
            renderCell: ({ row }) => props.sensorsResult.data?.find((sensor) => sensor.id === row.sensorId)?.name,
          },
          { 
            field: "sortOrder", 
            headerName: formatMessage(messages.sortOrderColumn),
            flex: 1, 
          },
          { 
            field: "visible", 
            headerName: formatMessage(messages.visibleColumn),
            flex: 0.5, 
            renderCell: (params) => {
                    return (
                        <Checkbox
                            name="visible"
                            value={params.value}
                            disabled={true}
                            onChange={() => {}}
                        />
                    );
                }
          },
          { 
            field: "conversionFactor", 
            headerName: formatMessage(messages.conversionFactorColumn),
            flex: 1, 
          },
          { 
            field: "minThreshold", 
            headerName: formatMessage(messages.minThresholdColumn),
            flex: 1, 
            renderCell: (params) => {
              return (
                  <>
                      {props.thresholdResult.data?.find((threshold) => threshold.readingTypeId === params.row.id && threshold.enableLowLimit === true)?.lowLimitValue}
                      {props.thresholdResult.data?.find((threshold) => threshold.readingTypeId === params.row.id && threshold.enableLowLimit === true)?.lowLimitInstructionId ? 
                      <Tooltip title={<Typography fontSize="12px">Instruction will be triggered</Typography>}>
                        <Box sx={{marginLeft: '5px'}}><NotificationsNoneIcon fontSize="small" /> </Box>
                      </Tooltip>
                       : ""}
                  </>
              )
          }
          },
          { 
            field: "maxThreshold", 
            headerName: formatMessage(messages.maxThresholdColumn),
            flex: 1, 
            renderCell: (params) => {
                return (
                    <>
                        {props.thresholdResult.data?.find((threshold) => threshold.readingTypeId === params.row.id && threshold.enableHighLimit === true)?.highLimitValue}
                        {props.thresholdResult.data?.find((threshold) => threshold.readingTypeId === params.row.id && threshold.enableHighLimit === true)?.highLimitInstructionId ? 
                        <Tooltip title={<Typography fontSize="12px">Instruction will be triggered</Typography>}>
                          <Box sx={{marginLeft: '5px'}}><NotificationsNoneIcon fontSize="small" /> </Box>
                        </Tooltip>
                        : ""}
                    </>
                )
            }
          },
          {
            field:"view", 
            headerName:"",
            flex: 1, 
            renderCell:(params) => {
                return(
                    <>
                      <Tooltip title={<Typography fontSize="12px"><FormattedMessage {...messages.editReadingType} /></Typography>}>
                        <IconButton
                          aria-label={formatMessage(messages.editReadingType)}
                          size="small"
                          sx={{
                              "&:hover": {
                              backgroundColor: colors.primary[500],
                              },
                              backgroundColor: colors.primary[700],
                              marginLeft: "10px",
                          }}
                          onClick={() => {
                              props.handleEditReadingTypeClick(params.row);
                          }}
                          >
                              <SettingsOutlinedIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={<Typography fontSize="12px"><FormattedMessage {...messages.editThresholds} /></Typography>}>
                        <IconButton
                          aria-label={formatMessage(messages.editThresholds)}
                          size="small"
                          sx={{
                              "&:hover": {
                              backgroundColor: colors.primary[500],
                              },
                              backgroundColor: colors.primary[700],
                              marginLeft: "10px",
                          }}
                          onClick={() => {
                              props.handleEditThresholdClick(params.row);
                          }}
                          >
                              <TuneOutlinedIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                  </>
                )
            },
          },
        ];
        
        const columnOrderLocalStorageName = 'readingType-columnOrder';
        const columnWidthsLocalStorageName = 'readingType-columnWidths';
        
        // Load persisted column order, widths, and sort order on mount
        function initColumnsFromLocalStorage(columns) {
          let columnsToFix = columns;
          // Load column order
          const savedColumnOrder = localStorage.getItem(columnOrderLocalStorageName);
          if (savedColumnOrder) {
            const orderArray = JSON.parse(savedColumnOrder);
            columnsToFix = reorderColumns(orderArray, columnsToFix);
          }
    
          // Load column widths
          const savedWidths = localStorage.getItem(columnWidthsLocalStorageName);
          if (savedWidths) {
              const widthMap = JSON.parse(savedWidths);
              columnsToFix = applyColumnWidths(widthMap, columnsToFix);
            }
          return columnsToFix;
        };
    
        // Reorder columns based on saved order
        const reorderColumns = (orderArray, columnsToFix) => {
          const reorderedColumns = orderArray.map((field) =>
              columnsToFix.find((col) => col.field === field)
          );
          reorderedColumns.push(...columnsToFix.filter((col) => !reorderedColumns.includes(col)));
          return reorderedColumns;
        };
    
        // Apply saved column widths
        const applyColumnWidths = (widthMap, columnsToFix) => {
          const updatedColumns = columnsToFix.map((col) => ({
            ...col,
            width: widthMap[col.field] || col.width, 
            flex: widthMap[col.field] !== undefined ? undefined : col.flex, 
          }));
          return updatedColumns;
        };
    
        // Handle column order change
        const handleColumnOrderChange = () => {
          if (!apiRef.current) {
            return;
          }
          const orderedColumns = apiRef.current.getVisibleColumns();
          const newColumnOrder = orderedColumns.map((col) => col.field);
          localStorage.setItem(columnOrderLocalStorageName, JSON.stringify(newColumnOrder));
        };
    
        // Handle column resize
        const handleColumnResize = (params) => {
          const { colDef, width } = params;
          const newWidths = JSON.parse(localStorage.getItem(columnWidthsLocalStorageName)) || {};
          newWidths[colDef.field] = width;
          localStorage.setItem(columnWidthsLocalStorageName, JSON.stringify(newWidths));
        };
    
        if(props.readingTypesResult?.isLoading || props.dataTypesResult?.isLoading || props.sensorsResult?.isLoading) {
            return <AxionControls.AxionBackdrop open={true} />
        }
    
        columns = initColumnsFromLocalStorage(columns);
    
        return (
            <Box padding={2} paddingTop={2}>
              <Paper
                sx={{
                  position: "relative",
                  borderRadius: "4px",
                }}
              >
                <Box 
                  m="0px 0 0 0"
                  height="76vh"
                  sx={{
                    "& .MuiDataGrid-root": {
                      border: "none",
                    },
                    "& .MuiDataGrid-cell": {
                    },
                    "& .name.column--cell": {
                      color: colors.greenAccent[300],
                    },
                    "& .MuiDataGrid-cellContent": {
                      fontSize: "13px",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: colors.primary[600],
                      borderBottom: "none",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                      backgroundColor: colors.primary[400],
                    },
                    "& .MuiDataGrid-footerContainer": {
                      borderTop: "none",
                      backgroundColor: colors.primary[600],
                    },
                    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                      color: `${colors.gray[100]} !important`,
                    },
                    "& .super-app-theme--11": {
                      bgcolor: `${colors.redAccent[500]} !important`,
                    },
                    "& .super-app-theme--undefined-stop": {
                      bgcolor: `${colors.redAccent[500]} !important`,
                    },
                    "& .MuiDataGrid-columnHeader": {
                      fontSize: "15px",
                    },
                  }}
                >
                  { (!props.readingTypesResult?.isLoading) && <DataGridPro
                    apiRef={apiRef}
                    localeText={translations.components.MuiDataGrid.defaultProps.localeText}
                    onColumnResize={handleColumnResize}
                    onColumnOrderChange={handleColumnOrderChange}
                    rows={props.readingTypesResult?.data}
                    sortModel={sortModelStorage}
                    onSortModelChange={(newModel) => setSortModelStorage(newModel)}
                    columns={columns}
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={(newModel) =>
                      setColumnVisibilityModel(newModel)
                    }
                    slotProps={{
                        panel: {
                        sx: {
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                backgroundColor: "#799c4a",
                                opacity: 0.3,
                            },
                            "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb": {
                                backgroundColor: "#799c4a",
                            },
                            "& .MuiDataGrid-panelFooter .MuiButtonBase-root": {
                                color: colors.primary[200],
                                fontSize: "0.75rem",
                                "&:hover": {
                                    color: colors.primary[300],
                                },
                            },
                            "& .MuiFormLabel-root.Mui-focused": {
                                color: colors.primary[200],
                            },
                        },
                        }
                    }}
                  /> }
                </Box>
              </Paper>
            </Box>
        );
}